// Contacts → bulk WhatsApp (client, 2026-11-05: "Interakt ke templates show
// karwao, select karu, aur wahi template se message jaye sabko").
//
// Pick an approved Interakt template, say what goes in each {{1}}, {{2}}…
// (the contact's name / company / salesperson, or fixed text), see who it
// reaches and who is left out, then send. It goes out in the background, one
// message at a time; progress shows under "Past sends". Admin / Super Admin.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { MessageCircle, RefreshCw, Send } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Spinner } from '@/shared/components/ui/Spinner';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { cn } from '@/shared/utils/utils';

interface Template {
  name: string; language: string; category: string; headerFormat: string; headerText: string;
  body: string; footer: string; buttons: string[]; varCount: number; sampleMediaUrl: string;
}
type Source = 'name' | 'company' | 'salesperson' | 'text';
interface Variable { source: Source; text: string; fallback: string }
interface Preview {
  selected: number; willSend: number; mode: 'live' | 'test'; max: number;
  skipped: { noMobile: number; duplicate: number; optedOut: number; recentlyMessaged: number; testMode: number; overLimit: number };
  samples: Array<{ name: string; phone: string; text: string }>;
}
interface Campaign {
  id: string; templateName: string; status: 'sending' | 'done' | 'stopped'; total: number; sent: number; failed: number;
  createdByName: string; createdAt: string; filterNote: string;
}

const SOURCES: Array<{ value: Source; label: string; fallback: string }> = [
  { value: 'name', label: "Contact's name", fallback: 'Sir/Madam' },
  { value: 'company', label: 'Company', fallback: 'your company' },
  { value: 'salesperson', label: 'Their salesperson', fallback: 'our team' },
  { value: 'text', label: 'Same text for everyone', fallback: '' },
];
const SKIP_LABEL: Record<keyof Preview['skipped'], string> = {
  noMobile: 'no Indian mobile number',
  duplicate: 'same number twice',
  optedOut: 'replied STOP',
  recentlyMessaged: 'got a bulk message in the last 7 days',
  testMode: 'not a test number (inbox is in Test mode)',
  overLimit: 'over the 1,000 per send limit',
};
const fill = (body: string, values: string[]) => body.replace(/\{\{(\d+)\}\}/g, (w, n) => values[Number(n) - 1] || w);

interface Props { open: boolean; onClose: () => void; ids: string[]; filterNote: string; onSent: () => void }

export function ContactsBulkWhatsappModal({ open, onClose, ids, filterNote, onSent }: Props) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'new' | 'past'>('new');
  const [name, setName] = useState('');
  const [vars, setVars] = useState<Variable[]>([]);
  const [preview, setPreview] = useState<Preview | null>(null);

  const templates = useQuery({
    queryKey: ['contacts-bulk', 'wa-templates'],
    queryFn: async () => (await api.get<{ templates: Template[]; mode: 'live' | 'test' }>('/api/contacts/bulk/whatsapp/templates')).data,
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });
  const campaigns = useQuery({
    queryKey: ['contacts-bulk', 'wa-campaigns'],
    queryFn: async () => (await api.get<{ items: Campaign[] }>('/api/contacts/bulk/whatsapp/campaigns')).data.items,
    enabled: open,
    refetchInterval: (q) => ((q.state.data ?? []).some((c) => c.status === 'sending') ? 4000 : false),
  });
  const template = (templates.data?.templates ?? []).find((t) => t.name === name);

  // A new template: one setting per {{n}}, starting with the contact's name.
  useEffect(() => {
    if (!template) { setVars([]); return; }
    setVars(Array.from({ length: template.varCount }, (_, i) => (i === 0
      ? { source: 'name', text: '', fallback: 'Sir/Madam' }
      : { source: 'text', text: '', fallback: '' })));
    setPreview(null);
  }, [template?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const setVar = (i: number, patch: Partial<Variable>) => {
    setPreview(null);
    setVars((v) => v.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  };
  const sampleValues = vars.map((v) => (v.source === 'text' ? v.text : v.source === 'name' ? 'Rajesh' : v.source === 'company' ? 'Shree Ganesh Industries' : 'Mahesh'));
  const ready = Boolean(template) && vars.every((v) => (v.source === 'text' ? v.text.trim() : v.fallback.trim()));

  const doPreview = useMutation({
    mutationFn: async () => (await api.post<Preview>('/api/contacts/bulk/whatsapp/preview', { ids, templateName: name, variables: vars })).data,
    onSuccess: setPreview,
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not prepare the preview.')),
  });
  const send = useMutation({
    mutationFn: async () => (await api.post<{ total: number }>('/api/contacts/bulk/whatsapp/send', { ids, templateName: name, variables: vars, filterNote })).data,
    onSuccess: (r) => {
      toast.success(`Sending "${name}" to ${r.total} contact(s) — follow it under Past sends.`, { duration: 7000 });
      qc.invalidateQueries({ queryKey: ['contacts-bulk', 'wa-campaigns'] });
      setPreview(null);
      setTab('past');
      onSent();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not start the bulk send.')),
  });

  const skippedRows = useMemo(() => (preview
    ? (Object.entries(preview.skipped) as Array<[keyof Preview['skipped'], number]>).filter(([, n]) => n > 0)
    : []), [preview]);

  return (
    <Modal open={open} onClose={onClose} size="2xl" title={<span className="inline-flex items-center gap-2"><MessageCircle className="h-4 w-4 text-success-text" /> Bulk WhatsApp — {ids.length} contact{ids.length === 1 ? "" : "s"} selected</span>}
      footer={tab === 'new' ? <>
        <Button variant="ghost" onClick={onClose}>Close</Button>
        <Button variant="outline" disabled={!ready || doPreview.isPending} onClick={() => doPreview.mutate()}>{doPreview.isPending ? 'Checking…' : 'Check who it reaches'}</Button>
        <Button disabled={!preview || !preview.willSend || send.isPending}
          onClick={() => { if (window.confirm(`Send the WhatsApp template "${name}" to ${preview?.willSend} contact(s)? This cannot be undone.`)) send.mutate(); }}>
          <Send className="h-4 w-4" /> {send.isPending ? 'Starting…' : `Send to ${preview?.willSend ?? '…'}`}
        </Button>
      </> : <Button variant="ghost" onClick={onClose}>Close</Button>}>
      <div className="mb-3 flex gap-2">
        {(['new', 'past'] as const).map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)}
            className={cn('rounded-lg border px-3 py-1.5 text-xs font-semibold', tab === t ? 'border-success bg-success text-white' : 'border-border text-on-surface-variant')}>
            {t === 'new' ? 'New send' : 'Past sends'}
          </button>
        ))}
        {templates.data?.mode === 'test' && <span className="self-center rounded-full bg-warning/15 px-2 py-0.5 text-[11px] font-semibold text-warning-text">Inbox is in Test mode — only test numbers receive it</span>}
      </div>

      {tab === 'past' ? <PastSends campaigns={campaigns.data ?? []} loading={campaigns.isLoading} /> : (
        <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
          <div>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-semibold text-on-surface-variant">Approved templates</span>
              <button type="button" title="Reload from Interakt" className="text-on-surface-variant hover:text-primary"
                onClick={async () => { await api.get('/api/contacts/bulk/whatsapp/templates?refresh=1'); qc.invalidateQueries({ queryKey: ['contacts-bulk', 'wa-templates'] }); }}>
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>
            {templates.isLoading ? <div className="py-8 text-center"><Spinner /></div> : templates.isError ? (
              <p className="text-xs text-danger-text">{getApiErrorMessage(templates.error, 'Could not load the templates.')}</p>
            ) : (
              <ul className="max-h-[420px] space-y-1 overflow-auto pr-1">
                {(templates.data?.templates ?? []).map((t) => (
                  <li key={t.name}>
                    <button type="button" onClick={() => setName(t.name)}
                      className={cn('w-full rounded-lg border px-3 py-2 text-left text-xs', name === t.name ? 'border-success bg-success/10' : 'border-border hover:border-success/50')}>
                      <span className="block font-semibold text-on-surface">{t.name}</span>
                      <span className="text-[11px] text-on-surface-variant">{t.category || '—'} · {t.language}{t.varCount ? ` · ${t.varCount} field${t.varCount > 1 ? 's' : ''}` : ''}{t.headerFormat === 'IMAGE' ? ' · image' : ''}</span>
                    </button>
                  </li>
                ))}
                {!(templates.data?.templates ?? []).length && <li className="text-xs text-on-surface-variant">No approved templates. Templates that carry a document (quotation / invoice PDF) are sent from the document itself.</li>}
              </ul>
            )}
          </div>

          <div className="space-y-3">
            {!template ? <p className="py-10 text-center text-sm text-on-surface-variant">Choose a template.</p> : (
              <>
                {template.category && template.category.toUpperCase() !== 'MARKETING' && (
                  <p className="rounded-lg bg-warning/10 px-3 py-2 text-xs text-warning-text">This template is <b>{template.category}</b>. Offers and win-back messages should use a <b>Marketing</b> template — Meta can block the number for misuse.</p>
                )}
                {template.varCount > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-on-surface-variant">What goes in each field</p>
                    {vars.map((v, i) => (
                      <div key={i} className="flex flex-wrap items-center gap-2">
                        <span className="w-10 font-mono text-xs">{`{{${i + 1}}}`}</span>
                        <select aria-label={`Field ${i + 1}`} className="h-8 rounded border border-border bg-surface px-2 text-xs" value={v.source}
                          onChange={(e) => { const s = e.target.value as Source; setVar(i, { source: s, fallback: SOURCES.find((x) => x.value === s)?.fallback ?? '' }); }}>
                          {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                        {v.source === 'text' ? (
                          <input className="h-8 min-w-[200px] flex-1 rounded border border-border bg-surface px-2 text-xs" placeholder="Text for everyone" value={v.text} maxLength={200} onChange={(e) => setVar(i, { text: e.target.value })} />
                        ) : (
                          <input className="h-8 w-44 rounded border border-border bg-surface px-2 text-xs" title="Used when a contact has none" placeholder="If missing" value={v.fallback} maxLength={100} onChange={(e) => setVar(i, { fallback: e.target.value })} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div>
                  <p className="mb-1 text-xs font-semibold text-on-surface-variant">How it looks</p>
                  <div className="whitespace-pre-wrap rounded-2xl rounded-tl-sm bg-success/10 px-4 py-3 text-sm text-on-surface">
                    {template.headerFormat === 'IMAGE' && template.sampleMediaUrl && <img src={template.sampleMediaUrl} alt="" className="mb-2 max-h-32 rounded" />}
                    {preview?.samples[0] ? preview.samples[0].text : (
                      <>
                        {template.headerFormat === 'TEXT' && template.headerText && <b className="mb-1 block">{template.headerText}</b>}
                        {fill(template.body, sampleValues)}
                        {template.footer && <span className="mt-2 block text-[11px] text-on-surface-variant">{template.footer}</span>}
                      </>
                    )}
                    {template.buttons.length > 0 && <span className="mt-2 block text-[11px] text-info-text">{template.buttons.join(' · ')}</span>}
                  </div>
                  {preview?.samples[0] && <p className="mt-1 text-[11px] text-on-surface-variant">As {preview.samples[0].name} ({preview.samples[0].phone}) will get it.</p>}
                </div>
                {preview && (
                  <div className="rounded-xl border border-border p-3 text-sm">
                    <p>Will go to <b>{preview.willSend}</b> of {preview.selected} selected.</p>
                    {skippedRows.length > 0 && (
                      <ul className="mt-1 list-disc pl-5 text-xs text-on-surface-variant">
                        {skippedRows.map(([k, n]) => <li key={k}>{n} left out — {SKIP_LABEL[k]}</li>)}
                      </ul>
                    )}
                    <p className="mt-2 text-[11px] text-on-surface-variant">Sent one by one in the background. Anyone who replies STOP is never sent a bulk message again.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function PastSends({ campaigns, loading }: { campaigns: Campaign[]; loading: boolean }) {
  if (loading) return <div className="py-10 text-center"><Spinner /></div>;
  if (!campaigns.length) return <p className="py-10 text-center text-sm text-on-surface-variant">No bulk WhatsApp sent yet.</p>;
  return (
    <ul className="space-y-2">
      {campaigns.map((c) => {
        const done = c.sent + c.failed;
        return (
          <li key={c.id} className="rounded-xl border border-border p-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-semibold">{c.templateName}</span>
              <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-semibold', c.status === 'sending' ? 'bg-info/10 text-info-text' : 'bg-success/10 text-success-text')}>
                {c.status === 'sending' ? `Sending… ${done}/${c.total}` : 'Finished'}
              </span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded bg-muted"><div className="h-full bg-success" style={{ width: `${c.total ? (done / c.total) * 100 : 0}%` }} /></div>
            <p className="mt-1 text-xs text-on-surface-variant">
              {c.sent} sent{c.failed ? ` · ${c.failed} failed` : ''} · by {c.createdByName || '—'} · {new Date(c.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
              {c.filterNote ? ` · ${c.filterNote}` : ''}
            </p>
          </li>
        );
      })}
    </ul>
  );
}
